import React, { useCallback, useEffect, useState } from 'react';
import { getProgramInsightsFilters } from '../../../api/v2/program_insights';

import DataRefreshBar from '../reportable_events/data_refresh_bar';
import Filters from './filters';
import ProgramEngagement from './program_engagement';
import EngagementContinuation from './engagement_continuation';
import OverallMentorSatisfaction from './overall_mentor_satisfaction';
import ProgramSurveyResults from './program_survey_results';
import ParticipantProgramJourney from './participant_program_journey';
import OnboardingProfile from './onboarding_profile';

import styles from './index.module.css';

const DEFAULT_FILTERS = {
  countries: [],
  siteIds: [],
  dateRange: 'all',
};

const isFiltered = (filters) =>
  filters.countries.length > 0 || filters.siteIds.length > 0 || filters.dateRange !== DEFAULT_FILTERS.dateRange;

// The dashboard home for CTMs and Sponsor Program Managers. The filter bar lives here and the
// chosen Country/Site/Date slice is handed to every metric card, so one change re-filters the whole
// page. Metric cards are added to the sections region by their own slices.
export default function ProgramInsights() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;

    getProgramInsightsFilters({ signal: controller.signal })
      .then((data) => {
        if (!isCurrent) return;
        setOptions(data);
        setError(null);
      })
      .catch((err) => {
        if (!isCurrent || err?.name === 'AbortError') return;
        setError('Filters did not load. Numbers below are all sites, all time. Refresh to filter.');
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, []);

  const applyFilters = useCallback((changes) => setFilters((current) => ({ ...current, ...changes })), []);
  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>Program metrics</p>
        <h1 className={styles.title}>Program Insights</h1>
        <p className={styles.subtitle}>
          Adoption, engagement, and satisfaction across the PatientPartner mentorship program.
        </p>
      </header>

      <DataRefreshBar dataRefresh={options?.data_refresh} />

      {options && (
        <Filters
          countries={options.countries}
          filters={filters}
          isFiltered={isFiltered(filters)}
          onChange={applyFilters}
          onReset={resetFilters}
          sites={options.sites}
        />
      )}

      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.sections}>
        {/* Metric cards are rendered here, each receiving `filters`. Added by their own slices. */}
        <ProgramEngagement filters={filters} />
        <EngagementContinuation filters={filters} />
        <ParticipantProgramJourney filters={filters} />
        <OverallMentorSatisfaction filters={filters} />
        <ProgramSurveyResults filters={filters} />
        <OnboardingProfile filters={filters} />
      </div>
    </section>
  );
}
